import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Dialog } from "primereact/dialog";
import React from "react";
import { useTranslation } from "react-i18next";
import { Measurement, MeasurementDetail } from "../shared/interfaces/Result";
import { useResultMeasurementStatusUpdate } from "../data/Results/useResultMeasurementStatusUpdate";
import { InputTextarea } from "primereact/inputtextarea";
import { useParams } from "react-router-dom";

interface MeasurementNewCommentDialogProps {
  open: boolean;
  measurementDetail: MeasurementDetail<Measurement>;
  captureId: number;
  onSuccess: (successMessage: string) => void;
  onError: (errorMessage: string) => void;
  onClose: () => void;
}

export function MeasurementNewCommentDialog(props: MeasurementNewCommentDialogProps) {
  const { t } = useTranslation();
  const { open, measurementDetail, captureId, onClose, onSuccess, onError } = props;

  const { customerId } = useParams<{ customerId: string }>();

  const [commentText, setCommentText] = React.useState("");

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value);
  };

  const handleCreateCommentClick = () => {
    commentCreate.mutate({
      ragStatus: measurementDetail.measurement.RAGStatus,
      oldRagStatus: measurementDetail.measurement.RAGStatus,
      comment: commentText,
      captureId: captureId,
      customerId: Number(customerId),
      panel360AssetId: measurementDetail.panel360AssetId,
      panelNo: measurementDetail.panelNo,
      measurementType: measurementDetail.measurementType,
      component: measurementDetail.measurement.ComponentType,
      subLocation: measurementDetail.measurement.SubLocation,
    });
  };

  const commentCreate = useResultMeasurementStatusUpdate({
    onSuccess: handleCreateCommentSuccess,
    onError: handleCreateCommentError,
  });

  function handleCreateCommentSuccess() {
    setCommentText("");
    onSuccess(t("common.commentCreateSuccess"));
    onClose();
  }

  function handleCreateCommentCancel() {
    setCommentText("");
    onClose();
  }

  function handleCreateCommentError() {
    onError(t("common.dataCreateError", { val: t("common.comment") }));
  }

  const header = <>{t("common.newComment")}</>;

  const footer = (
    <>
      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" onClick={handleCreateCommentClick} disabled={!commentText}>
          {t("common.addComment")}
        </Button>
        <Button variant="contained" color="secondary" onClick={handleCreateCommentCancel}>
          {t("common.cancel")}
        </Button>
      </Stack>
    </>
  );

  return (
    <Dialog
      header={
        <Typography variant="h6" component="p">
          {t("common.newComment")}
        </Typography>
      }
      visible={open}
      onHide={onClose}
    >
      {commentCreate.isLoading ? (
        <div className="d-flex justify-content-center">
          <CircularProgress size={65} />
        </div>
      ) : (
        <>
          <Dialog
            header={header}
            visible={open}
            onHide={handleCreateCommentCancel}
            footer={footer}
            breakpoints={{ "1100px": "40vw", "900px": "60vw", "600px": "90vw" }}
            style={{ width: "30vw" }}
          >
            <Stack direction="row" p={1} spacing={4}>
              <InputTextarea value={commentText} onChange={handleCommentChange} autoResize style={{ width: "100%" }} />
            </Stack>
          </Dialog>
        </>
      )}
    </Dialog>
  );
}
